import { EMPTY_OBJECT } from '../../utils';
import { ButtonGroup, ButtonVariants, ToggleButton } from '../Button';
import type { BoxProps } from '../Core';
import type { IconName } from '../Icons';
import { Tooltip } from '../Tooltip';
import { FormControlSizes } from './types';

export type ToggleHorizontalOption<ValueType extends string> = {
  value: ValueType;
  displayName?: string;
  tooltip?: React.ReactNode;
  buttonVariant?: ButtonVariants;
  disabled?: boolean;
  startIcon?: IconName;
  endIcon?: IconName;
};

export type ToggleHorizontalProps<TValue extends string> = {
  /** The list of options to display */
  options: Array<ToggleHorizontalOption<TValue>>;
  onChange: (value: TValue) => void;
  /** undefined is ok for unselected state */
  value: TValue | undefined;
  /** When value is empty, show this as selected (display only) */
  defaultValue?: TValue;
  size?: FormControlSizes;
  disabled?: boolean;
  boxProps?: BoxProps;
  'data-testid'?: string;
};

/**
 * Toggle Horizontal control - A horizontal list of buttons that can be toggled like a radio button bar
 * - Internally leverages the Tabs component
 * - Name inspired by the design team who called it a "ToggleHorizontal"
 * - When value is empty, defaultValue is shown as selected (display only)
 * @param options The list of options to display
 * @param onChange The function to call when an option is selected
 * @param value The value of the selected option
 */
export const ToggleHorizontal = <TValue extends string>({
  options,
  onChange,
  value,
  defaultValue,
  disabled = false,
  size = FormControlSizes.Default,
  boxProps = EMPTY_OBJECT,
  'data-testid': dataTestId,
}: ToggleHorizontalProps<TValue>) => {
  const effectiveValue = value == null || value === '' ? defaultValue : value;
  return (
    <ButtonGroup
      size={size}
      type="switch"
      aria-disabled={disabled ? 'true' : undefined}
      {...boxProps}
      display="flex"
      data-testid={dataTestId}
      role="radiogroup"
    >
      {options.map(option => {
        const selected = effectiveValue === option.value;
        const isOptionDisabled = disabled || option.disabled;
        return (
          <Tooltip
            tooltipTestID={`${dataTestId}:${option.value}:tooltip`}
            tooltip={option.tooltip}
            delay={500}
            targetStyle={{ flex: '1' }}
            key={'buttonGroup' + option.value}
          >
            <ToggleButton
              key={'buttonGroup-' + option.value}
              selected={selected}
              aria-checked={selected}
              disabled={isOptionDisabled}
              startIcon={option.startIcon}
              endIcon={option.endIcon}
              unselectedVariant={ButtonVariants.Transparent}
              selectedVariant={option.buttonVariant || ButtonVariants.Priority}
              size={size}
              onClick={() => !isOptionDisabled && onChange(option.value)}
              data-testid={dataTestId ? `${dataTestId}:${option.value}` : undefined}
              grouped
              role="radio"
            >
              {option.displayName}
            </ToggleButton>
          </Tooltip>
        );
      })}
    </ButtonGroup>
  );
};
